import React, { useContext } from "react";
import { Gesture, HighlightAlt } from "@mui/icons-material";

import { MapContext } from "../MapProvider";

import { IButtonProps } from "./Toolbar";
import ToolbarToggleButton from "./ToolbarToggleButton";

type SelectionMode = "polygon" | "lasso";

interface PolygonSelectButtonProps extends IButtonProps {
  /** The selection interaction mode. "polygon" uses click-to-place-vertices, "lasso" uses click-drag freehand. Defaults to "polygon". */
  selectionMode?: SelectionMode;
}

const modeConfig: Record<SelectionMode, { value: string; title: string; icon: React.ReactNode }> = {
  polygon: { value: "polygon_select", title: "Polygon select", icon: <HighlightAlt /> },
  lasso: { value: "lasso_select", title: "Lasso select", icon: <Gesture /> },
};

/**
 * A button that activates the polygon_select draw mode to select multiple features on the map.
 *
 * The `selectionMode` prop controls the interaction style:
 * - "polygon" (default): Click to place vertices, double-click to close.
 * - "lasso": Click and drag to draw a freehand selection area.
 */
const PolygonSelectButton: React.FC<PolygonSelectButtonProps> = ({ selectionMode = "polygon", title: titleProp, icon: iconProp }) => {
  const { map, drawControl } = useContext(MapContext);
  const { value, title: defaultTitle, icon: defaultIcon } = modeConfig[selectionMode];
  const title = titleProp ?? defaultTitle;
  const icon = iconProp ?? defaultIcon;

  const handleOnClick = () => {
    drawControl.current?.changeMode(value);
    map?.fire("draw.modechange", { type: "draw.modechange", mode: value });
  };

  return (
    <ToolbarToggleButton title={title} value={value} onClick={handleOnClick} data-testid={`toolbar-${value}-button`}>
      {icon}
    </ToolbarToggleButton>
  );
};

export default PolygonSelectButton;
